import { useToast } from '../components/ui/Toast';
import i18n from '../i18n';

// This provides a migration path from Mantine notifications to our custom toast system

interface NotificationOptions {
  title: string;
  message?: string;
  color?: 'green' | 'red' | 'blue' | 'yellow';
  autoClose?: number | boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
  /**
   * Skip the 3-second duplicate filter. For toasts that answer a user gesture
   * one to one (an undo after each removal): two identical gestures in a row
   * must give two toasts.
   */
  dedupe?: boolean;
}

// Create a singleton instance for non-hook contexts
let toastInstance: { showToast: (toast: any) => void } | null = null;

// Deduplication: Track recent notifications to prevent duplicates
interface RecentNotification {
  key: string;
  timestamp: number;
}

const recentNotifications: RecentNotification[] = [];
const DEDUP_WINDOW_MS = 3000; // 3 seconds window for deduplication

function getNotificationKey(title: string, message?: string): string {
  return `${title}::${message || ''}`;
}

function isDuplicate(title: string, message?: string): boolean {
  const key = getNotificationKey(title, message);
  const now = Date.now();

  // Clean up old notifications (older than dedup window)
  const cutoff = now - DEDUP_WINDOW_MS;
  const validIndex = recentNotifications.findIndex(n => n.timestamp >= cutoff);
  if (validIndex > 0) {
    recentNotifications.splice(0, validIndex);
  }

  // Check if this notification was recently shown
  const exists = recentNotifications.some(n => n.key === key);

  if (!exists) {
    // Add to recent notifications
    recentNotifications.push({ key, timestamp: now });
  }

  return exists;
}

export function setToastActions(actions: { showToast: (toast: any) => void }) {
  toastInstance = actions;
}

// Tracks the last error toast so the global mutation safety net (App.tsx)
// can tell whether a call site already surfaced the failure itself
let lastErrorToastAt = 0;

export function errorToastShownRecently(windowMs = 500): boolean {
  return Date.now() - lastErrorToastAt < windowMs;
}

export const notifications = {
  show: (options: NotificationOptions) => {
    if (!toastInstance) {
      console.warn('Toast actions not initialized. Make sure to call setToastActions in your app root.');
      return;
    }

    const { title, message, color } = options;

    // Prevent duplicate notifications within the dedup window
    if (options.dedupe !== false && isDuplicate(title, message)) {
      return;
    }

    // Map Mantine colors to our toast variants
    const variant = color === 'green' ? 'success' :
                   color === 'red' ? 'error' :
                   color === 'yellow' ? 'warning' :
                   'info';

    if (variant === 'error') {
      lastErrorToastAt = Date.now();
    }

    toastInstance.showToast({
      title,
      message,
      variant,
      duration: typeof options.autoClose === 'number' ? options.autoClose : 5000,
      action: options.action,
    });
  }
};

// Generic translated toast helpers. Reuse instead of hardcoding title/message.
// Override the default message via `message` arg when context matters.
type GenericKind = 'saved' | 'created' | 'deleted'
type GenericFailKind = 'loadFailed' | 'saveFailed' | 'deleteFailed'

export function toastSuccess(kind: GenericKind, message?: string) {
  notifications.show({
    title: i18n.t(`toast.${kind}.title`, { ns: 'common' }),
    message: message ?? i18n.t(`toast.${kind}.message`, { ns: 'common' }),
    color: 'green',
  })
}

export function toastError(kind: GenericFailKind, message?: string) {
  notifications.show({
    title: i18n.t(`toast.${kind}.title`, { ns: 'common' }),
    message: message ?? i18n.t(`toast.${kind}.message`, { ns: 'common' }),
    color: 'red',
  })
}

// Hook version for React components
export function useNotifications() {
  const { showToast } = useToast();

  return {
    show: (options: NotificationOptions) => {
      const { title, message, color } = options;

      // Prevent duplicate notifications within the dedup window
      if (options.dedupe !== false && isDuplicate(title, message)) {
        return;
      }

      const variant = color === 'green' ? 'success' :
                     color === 'red' ? 'error' :
                     color === 'yellow' ? 'warning' :
                     'info';

      if (variant === 'error') {
        lastErrorToastAt = Date.now();
      }

      showToast({
        title,
        message,
        variant,
        duration: typeof options.autoClose === 'number' ? options.autoClose : 5000,
        action: options.action,
      });
    }
  };
}